import { useState, useMemo } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Stack,
    Typography,
    Box,
    Alert,
    Avatar,
    Chip,
    useTheme,
    useMediaQuery,
    SwipeableDrawer,
} from '@mui/material';
import { Form, Formik, Field } from 'formik';
import * as Yup from 'yup';
import { ItemType, EventDetails, InventoryHistory } from '../schema';
import { recordConsumption } from '../utils/inventoryHistory';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';

interface RecordConsumptionDialogProps {
    open: boolean;
    onClose: () => void;
    itemType: ItemType;
    event?: EventDetails;
    existingHistory?: InventoryHistory | null;
    existingItemLocation?: { id: string; quantity: number } | null;
    totalTaken?: number; // Total quantity taken to event (from packing list or itemLocation)
}

interface FormValues {
    quantityUsed: number | '';
    quantityRemaining: number | '';
    note: string;
}

const validationSchema = Yup.object({
    quantityUsed: Yup.number().min(0, 'Quantity used must be 0 or greater').required('Enter either quantity used or remaining'),
    quantityRemaining: Yup.number().min(0, 'Quantity remaining must be 0 or greater').required('Enter either quantity used or remaining'),
    note: Yup.string(),
});

export default function RecordConsumptionDialog({
    open,
    onClose,
    itemType,
    event,
    existingHistory,
    existingItemLocation,
    totalTaken = 0,
}: RecordConsumptionDialogProps) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [error, setError] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Calculate initial values from existing history
    const initialValues = useMemo<FormValues>(() => {
        if (existingHistory) {
            const used = Math.abs(existingHistory.quantityChange || 0);
            const remaining = totalTaken - used;
            return {
                quantityUsed: used,
                quantityRemaining: Math.max(0, remaining),
                note: existingHistory.note || '',
            };
        }
        return {
            quantityUsed: '',
            quantityRemaining: totalTaken,
            note: '',
        };
    }, [existingHistory, totalTaken]);

    const handleSubmit = async (values: FormValues) => {
        if (!event) {
            setError('Event is required');
            return;
        }

        // Determine which value was entered (used or remaining)
        const quantityUsed = values.quantityUsed !== '' ? Number(values.quantityUsed) : totalTaken - Number(values.quantityRemaining);
        const quantityRemaining =
            values.quantityRemaining !== '' ? Number(values.quantityRemaining) : totalTaken - Number(values.quantityUsed);

        // Validate that used + remaining = total taken
        if (Math.abs(quantityUsed + quantityRemaining - totalTaken) > 0.01) {
            setError(`Used (${quantityUsed}) + Remaining (${quantityRemaining}) must equal Total Taken (${totalTaken})`);
            return;
        }

        if (quantityUsed < 0 || quantityRemaining < 0) {
            setError('Quantities cannot be negative');
            return;
        }

        try {
            setIsSubmitting(true);
            setError(null);
            await recordConsumption({
                itemTypeId: itemType.id,
                quantityUsed,
                note: values.note || undefined,
                eventId: event.id,
                existingHistory: existingHistory ? { id: existingHistory.id, quantityChange: existingHistory.quantityChange || 0 } : null,
                existingItemLocation: existingItemLocation ?? null,
            });
            onClose();
        } catch (err: any) {
            setError(err.message || 'Failed to record consumption');
        } finally {
            setIsSubmitting(false);
        }
    };

    const content = (
        <>
            <DialogTitle sx={{ pb: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocalGasStationIcon />
                    <Typography variant="h6">Consume</Typography>
                </Box>
            </DialogTitle>
            <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit} enableReinitialize>
                {({ values, setFieldValue, errors, touched: _touched, handleBlur }) => {
                    // Real-time calculation
                    const handleUsedChange = (value: string) => {
                        const used = value === '' ? '' : Number(value);
                        setFieldValue('quantityUsed', used);
                        if (used !== '' && totalTaken > 0) {
                            const remaining = Math.max(0, totalTaken - Number(used));
                            setFieldValue('quantityRemaining', remaining);
                        }
                    };

                    const handleRemainingChange = (value: string) => {
                        const remaining = value === '' ? '' : Number(value);
                        setFieldValue('quantityRemaining', remaining);
                        if (remaining !== '' && totalTaken > 0) {
                            const used = Math.max(0, totalTaken - Number(remaining));
                            setFieldValue('quantityUsed', used);
                        }
                    };

                    const quantityUsed = values.quantityUsed !== '' ? Number(values.quantityUsed) : 0;
                    const quantityRemaining = values.quantityRemaining !== '' ? Number(values.quantityRemaining) : 0;
                    const calculatedTotal = quantityUsed + quantityRemaining;
                    const isValidTotal = Math.abs(calculatedTotal - totalTaken) < 0.01;

                    const exceedsAvailable = quantityUsed > totalTaken;

                    return (
                        <Form>
                            <DialogContent>
                                <Stack spacing={3}>
                                    {/* Item Info */}
                                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                        {itemType.photoUrl ? (
                                            <Avatar src={itemType.photoUrl} variant="rounded" sx={{ width: 56, height: 56 }} />
                                        ) : (
                                            <Avatar variant="rounded" sx={{ width: 56, height: 56, bgcolor: 'primary.light' }}>
                                                <LocalGasStationIcon />
                                            </Avatar>
                                        )}
                                        <Box sx={{ flex: 1 }}>
                                            <Typography variant="h6">{itemType.name}</Typography>
                                            {itemType.category && (
                                                <Chip
                                                    label={itemType.category.name}
                                                    size="small"
                                                    sx={{ mt: 0.5 }}
                                                    color={itemType.category.color ? 'primary' : 'default'}
                                                />
                                            )}
                                        </Box>
                                    </Box>

                                    {/* Event Context */}
                                    {event && (
                                        <Box>
                                            <Typography variant="body2" color="text.secondary">
                                                Event: <strong>{event.name}</strong>
                                            </Typography>
                                        </Box>
                                    )}

                                    {/* Summary */}
                                    <Box sx={{ bgcolor: 'action.hover', p: 2, borderRadius: 1 }}>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                            Total Taken to Event
                                        </Typography>
                                        <Typography variant="h6">{totalTaken}</Typography>
                                    </Box>

                                    {/* Dual Input Fields */}
                                    <Box>
                                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                                            Enter either quantity used OR quantity remaining
                                        </Typography>
                                        <Stack spacing={2}>
                                            <Field name="quantityUsed">
                                                {({ field, meta }: any) => (
                                                    <TextField
                                                        {...field}
                                                        label="Quantity Used"
                                                        type="number"
                                                        fullWidth
                                                        onChange={e => {
                                                            handleUsedChange(e.target.value);
                                                        }}
                                                        onBlur={handleBlur}
                                                        error={meta.touched && Boolean(meta.error)}
                                                        helperText={meta.touched && meta.error}
                                                        inputProps={{ min: 0, step: 1 }}
                                                    />
                                                )}
                                            </Field>

                                            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                                                OR
                                            </Typography>

                                            <Field name="quantityRemaining">
                                                {({ field, meta }: any) => (
                                                    <TextField
                                                        {...field}
                                                        label="Quantity Remaining"
                                                        type="number"
                                                        fullWidth
                                                        onChange={e => {
                                                            handleRemainingChange(e.target.value);
                                                        }}
                                                        onBlur={handleBlur}
                                                        error={meta.touched && Boolean(meta.error)}
                                                        helperText={meta.touched && meta.error}
                                                        inputProps={{ min: 0, step: 1 }}
                                                    />
                                                )}
                                            </Field>
                                        </Stack>
                                    </Box>

                                    {/* Calculated Values Display */}
                                    <Box sx={{ bgcolor: 'action.hover', p: 2, borderRadius: 1 }}>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                            Calculated Values
                                        </Typography>
                                        <Stack direction="row" spacing={2}>
                                            <Box sx={{ flex: 1 }}>
                                                <Typography variant="body2" color="text.secondary">
                                                    Used
                                                </Typography>
                                                <Typography variant="h6">{quantityUsed}</Typography>
                                            </Box>
                                            <Box sx={{ flex: 1 }}>
                                                <Typography variant="body2" color="text.secondary">
                                                    Remaining
                                                </Typography>
                                                <Typography variant="h6">{quantityRemaining}</Typography>
                                            </Box>
                                        </Stack>
                                        {!isValidTotal && totalTaken > 0 && (
                                            <Alert severity="warning" sx={{ mt: 1 }}>
                                                Used ({quantityUsed}) + Remaining ({quantityRemaining}) = {calculatedTotal}, but Total Taken
                                                is {totalTaken}
                                            </Alert>
                                        )}
                                    </Box>

                                    {/* Warning if exceeds available */}
                                    {exceedsAvailable && (
                                        <Alert severity="error">
                                            Cannot use {quantityUsed} items. Only {totalTaken} {totalTaken === 1 ? 'item' : 'items'} taken
                                            to this event.
                                        </Alert>
                                    )}

                                    {/* Note Field */}
                                    <Field name="note">
                                        {({ field, meta }: any) => (
                                            <TextField
                                                {...field}
                                                label="Note (optional)"
                                                multiline
                                                rows={3}
                                                fullWidth
                                                error={meta.touched && Boolean(meta.error)}
                                                helperText={meta.touched && meta.error}
                                            />
                                        )}
                                    </Field>

                                    {error && <Alert severity="error">{error}</Alert>}
                                </Stack>
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={onClose} disabled={isSubmitting}>
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    disabled={isSubmitting || !isValidTotal || exceedsAvailable || Object.keys(errors).length > 0}
                                >
                                    {isSubmitting ? 'Saving...' : existingHistory ? 'Update' : 'Record'}
                                </Button>
                            </DialogActions>
                        </Form>
                    );
                }}
            </Formik>
        </>
    );

    if (isMobile) {
        return (
            <SwipeableDrawer
                anchor="bottom"
                open={open}
                onClose={onClose}
                onOpen={() => {}}
                disableSwipeToOpen
                PaperProps={{
                    sx: {
                        borderTopLeftRadius: 16,
                        borderTopRightRadius: 16,
                        maxHeight: '90vh',
                        pb: 2,
                    },
                }}
            >
                <Box sx={{ px: 2, pt: 1, pb: 2 }}>{content}</Box>
            </SwipeableDrawer>
        );
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            {content}
        </Dialog>
    );
}
